import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { alsLokalesDatum, api, errorText, formatDateTime, formatFrist } from '../api';
import { useAuth } from '../auth';
import { ticketKunde } from '../components/Tickets';
import { PositionenEditor, leerePosition, positionenFuerApi, type PositionEingabe } from '../components/Vorgang';
import { Badge, Card, Dialog, Field, Loading, Notice, TYP_LABEL, TYP_SYMBOL } from '../components/ui';
import type { BenutzerKurz, Ticket } from '../types';

type Aktion = 'erledigen' | 'verwerfen' | 'verschieben' | 'weitergeben' | 'wiedereroeffnen' | null;

const NOTIZ_ART: Record<string, string> = {
  notiz: 'Notiz',
  verschoben: 'Frist verschoben',
  nicht_erreicht: 'Nicht erreicht',
  weitergegeben: 'Weitergegeben',
  abschluss: 'Abgeschlossen',
  wiedereroeffnet: 'Wieder geöffnet',
};

export function TicketDetailPage() {
  const { id } = useParams();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [inBestellung, setInBestellung] = useState<PositionEingabe[] | null>(null);
  const [t, setT] = useState<Ticket | null>(null);
  const [fehler, setFehler] = useState('');
  const [aktion, setAktion] = useState<Aktion>(null);
  const [text, setText] = useState('');
  const [datum, setDatum] = useState('');
  const [an, setAn] = useState('');
  const [notiz, setNotiz] = useState('');
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(() => {
    api<Ticket>(`/api/tickets/${id}`)
      .then(setT)
      .catch((e) => setFehler(errorText(e)));
  }, [id]);
  useEffect(laden, [laden]);
  useEffect(() => {
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then(setBenutzer);
  }, []);

  async function ausfuehren(pfad: string, body: unknown) {
    setBusy(true);
    setFehler('');
    try {
      setT(await api<Ticket>(`/api/tickets/${id}/${pfad}`, { method: 'POST', body }));
      setAktion(null);
      setText('');
      setNotiz('');
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const oeffne = (a: Aktion) => {
    setFehler('');
    setText('');
    if (a === 'verschieben') setDatum(alsLokalesDatum(new Date(Date.now() + 24 * 3600_000)));
    if (a === 'weitergeben') setAn('');
    setAktion(a);
  };

  if (!t) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;

  const offen = t.status === 'offen';
  const istAdmin = user?.rolle === 'admin';
  const darfErledigen = offen && (istAdmin || t.zustaendig_id === user?.id);
  const darfVerwerfen = offen && (istAdmin || t.angelegt_von === user?.id);
  const nummer = t.kunde_telefone?.[0] ?? t.telefon;

  return (
    <>
      <div className="kopf">
        <div>
          <div className="muted small">
            <Link to="/tickets">Tickets</Link> / <span className="mono">{t.nummer}</span>
          </div>
          <h1>
            {TYP_SYMBOL[t.typ]} {t.titel}
          </h1>
        </div>
        <span className="spacer" />
        {offen ? (
          t.ueberfaellig ? <Badge kind="bad">überfällig {formatFrist(t.faellig_am)}</Badge> : <Badge kind="info">fällig {formatFrist(t.faellig_am)}</Badge>
        ) : (
          <Badge kind={t.status === 'erledigt' ? 'ok' : 'default'}>{t.status}</Badge>
        )}
        {t.eskaliert_am && offen && <Badge kind="bad">eskaliert</Badge>}
      </div>

      {fehler && !aktion && <Notice kind="error">{fehler}</Notice>}

      <div className="spalten haupt-links">
        <div className="stack">
          <Card title="Was zu tun ist">
            <div className="stack" style={{ gap: 10 }}>
              <div>{t.beschreibung ? <div style={{ whiteSpace: 'pre-wrap' }}>{t.beschreibung}</div> : <span className="muted">Keine Details erfasst.</span>}</div>
              {offen && (
                <div className="row">
                  {t.typ === 'rueckruf' && nummer && (
                    <a className="button" href={`tel:${nummer.replace(/[^\d+]/g, '')}`}>
                      ☎ {nummer}
                    </a>
                  )}
                  {darfErledigen && (
                    <button className="ok" onClick={() => oeffne('erledigen')}>
                      {t.typ === 'rueckruf' ? 'Erreicht – erledigt' : 'Erledigt'}
                    </button>
                  )}
                  {t.typ === 'rueckruf' && (
                    <button disabled={busy} onClick={() => void ausfuehren('nicht-erreicht', {})}>
                      Nicht erreicht
                    </button>
                  )}
                  <button onClick={() => oeffne('verschieben')}>Frist verschieben</button>
                  <button onClick={() => oeffne('weitergeben')}>Weitergeben</button>
                  {darfErledigen && t.kunde_id && can('admin', 'verkauf', 'techniker') && (
                    <button onClick={() => setInBestellung([{ ...leerePosition(), bezeichnung: t.titel }])}>In Bestellung überführen</button>
                  )}
                  {darfVerwerfen && (
                    <button className="gefahr" onClick={() => oeffne('verwerfen')}>
                      Verwerfen
                    </button>
                  )}
                </div>
              )}
              {offen && !darfErledigen && (
                <div className="small muted">Erledigen kann nur {t.zustaendig}. Du kannst das Ticket aber übernehmen lassen oder eine Notiz schreiben.</div>
              )}
              {!offen && (
                <div className="notice success" style={{ marginBottom: 0 }}>
                  <strong>{t.status === 'erledigt' ? 'Erledigt' : 'Verworfen'}</strong> am {formatDateTime(t.abgeschlossen_am)}: {t.abschluss_notiz}
                  <div style={{ marginTop: 6 }}>
                    <button className="klein" onClick={() => oeffne('wiedereroeffnen')}>
                      Wieder öffnen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card title="Verlauf">
            {offen && (
              <div className="row" style={{ flexWrap: 'nowrap', marginBottom: 12 }}>
                <input placeholder="Notiz hinzufügen …" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
                <button disabled={!notiz.trim() || busy} onClick={() => void ausfuehren('notiz', { text: notiz })}>
                  Speichern
                </button>
              </div>
            )}
            <div className="verlauf">
              {(t.notizen ?? []).map((n) => (
                <div className="eintrag" key={n.id}>
                  <div className="wann">
                    {formatDateTime(n.angelegt_am)}
                    <br />
                    {n.benutzer}
                  </div>
                  <div>
                    {n.art !== 'notiz' && <Badge>{NOTIZ_ART[n.art] ?? n.art}</Badge>} {n.text}
                  </div>
                </div>
              ))}
              <div className="eintrag">
                <div className="wann">
                  {formatDateTime(t.angelegt_am)}
                  <br />
                  {t.angelegt_von_name}
                </div>
                <div>Ticket angelegt</div>
              </div>
            </div>
          </Card>
        </div>

        <Card title="Angaben">
          <div className="stack" style={{ gap: 10 }}>
            <div>
              <div className="small muted">Kunde</div>
              {t.kunde_id ? (
                <Link to={`/kunden/${t.kunde_id}`}>
                  {ticketKunde(t)} <span className="mono muted">{t.kundennummer}</span>
                </Link>
              ) : (
                <span>Unbekannter Anrufer · {t.telefon}</span>
              )}
              {t.kunde_telefone && t.kunde_telefone.length > 0 && <div className="small">{t.kunde_telefone.join(' · ')}</div>}
            </div>
            <div>
              <div className="small muted">Art</div>
              {TYP_LABEL[t.typ]}
              {t.rueckruf_versuche > 0 && <span className="muted"> · {t.rueckruf_versuche}× nicht erreicht</span>}
            </div>
            <div>
              <div className="small muted">Zuständig</div>
              {t.zustaendig}
            </div>
            <div>
              <div className="small muted">Fällig</div>
              {formatDateTime(t.faellig_am)}
            </div>
          </div>
        </Card>
      </div>

      {inBestellung && (
        <Dialog
          titel="In Bestellung überführen"
          breit
          onClose={() => setInBestellung(null)}
          aktionen={
            <>
              <button onClick={() => setInBestellung(null)}>Abbrechen</button>
              <button
                className="primary"
                disabled={busy || positionenFuerApi(inBestellung).length === 0}
                onClick={async () => {
                  setBusy(true);
                  setFehler('');
                  try {
                    const b = await api<{ id: string }>(`/api/tickets/${t.id}/in-bestellung`, {
                      method: 'POST',
                      body: { positionen: positionenFuerApi(inBestellung) },
                    });
                    navigate(`/bestellungen/${b.id}`);
                  } catch (e) {
                    setFehler(errorText(e));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Bestellung anlegen
              </button>
            </>
          }
        >
          {fehler && <Notice kind="error">{fehler}</Notice>}
          <p className="small muted" style={{ margin: 0 }}>
            Das Ticket wird dabei mit Verweis auf die neue Bestellung erledigt. Ab dann läuft alles über die Bestellung mit ihrer eigenen Wiedervorlage.
          </p>
          <PositionenEditor positionen={inBestellung} onChange={setInBestellung} />
        </Dialog>
      )}
      {aktion && (
        <Dialog
          titel={
            {
              erledigen: 'Ticket erledigen',
              verwerfen: 'Ticket verwerfen',
              verschieben: 'Frist verschieben',
              weitergeben: 'An Kollegen weitergeben',
              wiedereroeffnen: 'Ticket wieder öffnen',
            }[aktion]
          }
          onClose={() => setAktion(null)}
          aktionen={
            <>
              <button onClick={() => setAktion(null)}>Abbrechen</button>
              <button
                className="primary"
                disabled={busy || (aktion === 'weitergeben' ? !an : aktion === 'verschieben' ? text.trim().length < 3 || !datum : text.trim().length < 3)}
                onClick={() => {
                  if (aktion === 'erledigen') void ausfuehren('abschliessen', { status: 'erledigt', notiz: text });
                  if (aktion === 'verwerfen') void ausfuehren('abschliessen', { status: 'verworfen', notiz: text });
                  if (aktion === 'verschieben') void ausfuehren('verschieben', { faelligAm: new Date(datum).toISOString(), begruendung: text });
                  if (aktion === 'weitergeben') void ausfuehren('weitergeben', { zustaendigId: an, notiz: text || undefined });
                  if (aktion === 'wiedereroeffnen') void ausfuehren('wiedereroeffnen', { begruendung: text });
                }}
              >
                Bestätigen
              </button>
            </>
          }
        >
          {fehler && <Notice kind="error">{fehler}</Notice>}
          {aktion === 'verschieben' && (
            <Field label="Neue Frist">
              <input type="datetime-local" value={datum} onChange={(e) => setDatum(e.target.value)} />
            </Field>
          )}
          {aktion === 'weitergeben' && (
            <Field label="An">
              <select value={an} onChange={(e) => setAn(e.target.value)}>
                <option value="">— bitte wählen —</option>
                {benutzer
                  .filter((b) => b.id !== t.zustaendig_id)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          <Field
            label={
              aktion === 'erledigen'
                ? 'Was wurde gemacht? (Pflicht)'
                : aktion === 'weitergeben'
                  ? 'Hinweis für den Kollegen (optional)'
                  : 'Begründung (Pflicht)'
            }
          >
            <textarea value={text} onChange={(e) => setText(e.target.value)} autoFocus />
          </Field>
        </Dialog>
      )}
    </>
  );
}
